import { ChatRounded, InsightsRounded } from '@mui/icons-material';
import { Box, SxProps, Theme, Typography, alpha } from '@mui/material';

interface FeedbackCountCitationContainerProps {
  isDisabled: boolean;
  isOpen: boolean;
  children: React.ReactNode;
  variant: 'circular' | 'chatBubble' | 'insights' | 'feedback';
  isLabelType: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const FeedbackCountCitationContainer = ({
  isDisabled,
  isOpen,
  children,
  isLabelType,
  variant,
  onClick,
}: FeedbackCountCitationContainerProps) => {
  const getTextColor = (theme: Theme) => {
    if (isDisabled) {
      return theme.palette.text.disabled;
    }

    return isOpen
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main;
  };

  const getTextBgColor = (theme: Theme) => {
    if (variant === 'feedback') {
      return undefined;
    }

    if (variant === 'insights') {
      return alpha(theme.palette.primary.main, 0.15);
    }

    if (isDisabled) {
      return theme.palette.action.disabledBackground;
    }
    return isOpen
      ? theme.palette.primary.main
      : alpha(theme.palette.primary.main, 0.15);
  };

  const getHoverStylesForVariant = () => {
    if (isDisabled || isOpen) {
      return {};
    }

    if (variant === 'feedback') {
      return {
        backgroundColor: (theme: Theme) =>
          alpha(theme.palette.primary.main, 0.15),
      };
    }

    return {
      backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.3),
    };
  };

  const getStylesForVariant = (): SxProps<Theme> => {
    if (variant === 'feedback') {
      return {
        mx: 0.3,
        px: 0.3,
        display: 'inline-flex',
        borderRadius: 0.5,
        height: 20,
        minWidth: 20,
      };
    }

    if (variant === 'insights') {
      return {
        mx: 0.25,
        px: 0.25,
        display: 'inline-flex',
        borderRadius: 0.5,
        height: 18,
        minWidth: 18,
      };
    }

    if (variant === 'chatBubble') {
      return {
        padding: 0.2,
        clipPath:
          'polygon(0% 0%, 100% 0%, 100% 75%, 6px 75%, 0 100%, 0 75%, 0% 75%)',
        height: '22px',
        width: 'max-content',
        ml: 0.5,
        borderRadius: '2px',
        position: 'relative',
        minWidth: '20px',
        display: 'flex',
      };
    }

    if (variant === 'circular') {
      return {
        mx: 0.25,
        px: 0.25,
        display: 'inline-flex',
        borderRadius: isLabelType ? 0.5 : '50%',
        height: 18,
        minWidth: 18,
      };
    }

    return {};
  };

  const renderContent = () => {
    if (variant === 'feedback') {
      return (
        <ChatRounded
          sx={{
            fontSize: '1.2rem',
            color: (theme) => alpha(theme.palette.primary.main, 0.9),
          }}
        />
      );
    }

    if (variant === 'insights') {
      return (
        <InsightsRounded
          sx={{
            fontSize: '1rem',
            color: (theme) => alpha(theme.palette.primary.main, 0.9),
          }}
        />
      );
    }

    return children;
  };

  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{
        color: getTextColor,
        backgroundColor: getTextBgColor,
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isDisabled ? 'progress' : 'pointer',
        transform: `translateY(-${variant === 'feedback' ? '4' : '8'}px)`,
        '&:hover': {
          ...getHoverStylesForVariant(),
        },
        ...getStylesForVariant(),
      }}
    >
      <Typography
        color="inherit"
        textAlign="center"
        variant="caption"
        fontWeight={500}
        sx={{
          mt: variant === 'chatBubble' ? -0.7 : 0,
        }}
      >
        {renderContent()}
      </Typography>
    </Box>
  );
};
